import Green from "../Assets/Images/smiley_green.png";
import Red from "../Assets/Images/smiley_red.png";
import Yellow from "../Assets/Images/smiley_yellow.png";

export const formatNumber = (number) => {
  return isNaN(number) || !isFinite(number)
    ? "0"
    : Math.round(number).toLocaleString();
};

export const checkFormat = (value) => {
  const numericValue = parseFloat(value);
  return !isNaN(numericValue) ? numericValue : 0;
};

export function constrainValue(value, low, high) {
  return Math.max(Math.min(parseFloat(value), high), low);
}

export const MitigationOptions = [
  { key: "Financial Statement", value: "Financial Statement", recommended: false },
  { key: "Comfort Letter", value: "Comfort Letter", recommended: false },
  {
    key: "Parent Company Commitment and Insurance",
    value: "Parent Commitment",
    recommended: false
  },
  { key: "Owners' guarantee", value: "Owners' guarantee", recommended: false },
  { key: "Government guarantee", value: "Government guarantee", recommended: false },
  { key: "Collateral", value: "Collateral", recommended: false }
];

export function normalizePaydex(value) {
  return constrainValue(parseFloat(value)?.toFixed(1), 1, 10);
}

export function formatArcScore(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return constrainValue(parseFloat(value)?.toFixed(1), 1, 10);
}

export function formatCurrency(value) {
  let numericValue = parseFloat(value);
  if (isNaN(numericValue)) numericValue = 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericValue);
}

// Numeric zero is an explicit credit decision. Missing/invalid means the
// underwriting flow has no decision to display yet.
export function formatRecommendedLimit(value) {
  if (value === null || value === undefined || value === "") return "-";
  const numericValue = typeof value === "string"
    ? Number(value.replace(/,/g, "").trim())
    : Number(value);
  return Number.isFinite(numericValue) ? formatCurrency(numericValue) : "-";
}

export const formatCurrencyValue = (value) => {
  let numericValue = parseFloat(value);
  if (isNaN(numericValue)) return 0;
  return Math.round(numericValue);
};

export function formatScoreValue(value) {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : parseFloat(value.toFixed(1));
  }
  return value;
}


export function processAgingData(Data, agingDays) {
  const agingData = {};
  Object.keys(Data).forEach((key) => {
    if (key.startsWith("AR Balance Aging")) {
      const agingPeriod = key.replace("AR Balance Aging ", "");
      agingData[`Aging${agingPeriod}`] = Data[key] || 0;
    }
  });
  const preferences = [agingDays.age1, agingDays.age2, agingDays.age3];

  const numericPreferences = preferences
    .map((pref) =>
      typeof pref === "string" ? parseInt(pref.replace("+", "")) : pref
    )
    .sort((a, b) => a - b);

  const result = {};
  preferences.forEach((pref) => (result[pref] = 0));

  const numericAgingData = new Map();
  for (const [key, value] of Object.entries(agingData)) {
    let num = parseInt(key.slice(5).replace("+", ""));
    let isPlus = key.includes("+");
    if (isPlus) {
      num += "+";
    }
    numericAgingData.set(num, parseFloat(value) || 0);
  }

  const agingPeriods = Array.from(numericAgingData.keys()).sort((a, b) => {
    const isAPlus = typeof a === "string" && a.includes("+");
    const isBPlus = typeof b === "string" && b.includes("+");
    if (isAPlus && !isBPlus) return 1;
    if (!isAPlus && isBPlus) return -1;
    return parseInt(a) - parseInt(b);
  });

  agingPeriods.forEach((period) => {
    let value = numericAgingData.get(period);
    let targetPref = null;

    if (typeof period === "string" && period.includes("+")) {
      targetPref = preferences[preferences.length - 1];
    } else {
      for (let i = 0; i < numericPreferences.length; i++) {
        if (period <= numericPreferences[i]) {
          targetPref = preferences[i];
          break;
        }
      }
    }

    if (targetPref) {
      result[targetPref] += value;
    }
  });

  return result;
}

export function normalizeRowData(summary) {
  return summary.map((row) => {
    const normalizedRow = { ...row };
    for (const key in normalizedRow) {
      if (
        key !== "Company Name" &&
        key !== "companyId" &&
        key !== "createdAt" &&
        key !== "lastUnderwritingDate" &&
        key !== "underwritingStartedAt" &&
        typeof normalizedRow[key] === "string"
      ) {
        const numericValue = parseFloat(normalizedRow[key].replace(/,/g, ""));
        normalizedRow[key] = isNaN(numericValue)
          ? normalizedRow[key]
          : numericValue;
      }
    }
    
    // Special condition for requestedCreditLimit
    if (row.requestedCreditLimit !== undefined) {
      const numericValue = parseFloat(row.requestedCreditLimit?.toString().replace(/,/g, ""));
      normalizedRow["Requested Limit"] = isNaN(numericValue)
        ? row.requestedCreditLimit
        : numericValue;
    }
    
    return normalizedRow;
  });
}

export async function fetchFileFromS3(
  key,
  bucket = "crediarc-content01853-production" // ENV
) {
  const s3Url = `https://${bucket}.s3.amazonaws.com/${key}`;
  console.log("Fetching data from S3 URL:", s3Url);

  try {
    const response = await fetch(s3Url, {
      method: "GET",
      headers: {
        "If-Modified-Since": new Date(0).toUTCString(),
      },
    });

    if (response.ok) {
      const data = await response.json();
      console.log(`Data fetched successfully for key: ${key} -->`, data);
      return data;
    }
  } catch (error) {
    console.error(`Failed to fetch data for key: ${key} -->`, error);
    throw error;
  }
}


// Treat an underwriting as "active" only if the in-progress flag is set AND it started
// recently. A run that crashed / was killed mid-flow leaves underwritingInProgress=true
// forever; without this guard the UI shows "Underwriting…" indefinitely (the Carvana
// 20-minute bug) and keeps polling the full summaries payload every 15s. After this
// window we consider the flag stale and stop.
//
// Why 30 min (not 8): an AR import stamps underwritingInProgress + underwritingStartedAt on
// EVERY company at creation time, all at once, but underwriting scores them sequentially. A
// company still queued 8 min after a large import is legitimately pending — an 8-min cap wrongly
// marked it idle, freezing its refresh spinner AND stopping the dashboard poll (so progress never
// surfaced). The currently-running company gets its underwritingStartedAt re-stamped to "now" by
// the worker, so this only governs how long a QUEUED company keeps spinning; 30 min covers a
// realistic import queue while still ending the poll for a genuinely dead run.
export const UNDERWRITING_STALE_MS = 30 * 60 * 1000;
export const isUnderwritingActive = (entity) => {
  if (!entity || !entity.underwritingInProgress) return false;
  const started = entity.underwritingStartedAt ? Date.parse(entity.underwritingStartedAt) : NaN;
  if (Number.isFinite(started) && Date.now() - started > UNDERWRITING_STALE_MS) return false;
  return true;
};

export const getScoreAttributes = (score) => {
  let backgroundColor;
  let color;
  let smiley;
  let darkColor;
  if (score >= 1 && score < 4) {
    backgroundColor = "#60AC5033";
    darkColor = "#3AA623";
    color = "#60AC50";
    smiley = Green;
  } else if (score >= 4 && score < 8) {
    backgroundColor = "#FFB30033";
    darkColor = "#9E6F00";
    color = "#E7BF3C";
    smiley = Yellow;
  } else if (score >= 8) {
    backgroundColor = "#F25A4833";
    darkColor = "#F25A48";
    smiley = Red;
    color = "#F25A48";
  } else {
    backgroundColor = "#f0f0f0";
    color = "#2F3D63";
    darkColor = "#000000";
  }
  return { color, darkColor, backgroundColor, smiley };
};
